import { RefObject, useState } from 'react';
import useResizeObserver from '@react-hook/resize-observer';

// Note: `useRemainingSpace` can also do this, but:
// - it's named confusingly
// - it measures both height and width and triggers state updates when *either*
//   of them changes, which leads to a lot of unwanted re-renders if you only
//   care about height
// - it measures contentBox size instead of borderBox size, which is usually
//   not what you want

/**
 * Hook that measures the height of a given element and uses a
 * ResizeObserver to re-measure whenever its height changes.
 *
 * @returns The height of the element, or null if the element's height hasn't
 * been measured yet (e.g. on very first render).
 */
export default function useMeasureHeight<T extends HTMLElement = HTMLElement>(
  elementRef: RefObject<T>,
): number | null {
  const [height, setHeight] = useState<number | null>(null);

  useResizeObserver(elementRef, (entry) => {
    if (entry.borderBoxSize[0]) {
      const newHeight = entry.borderBoxSize[0].blockSize;
      if (newHeight !== height) {
        setHeight(newHeight);
      }
    }
  });

  return height;
}
